/**
 * SyncManager: one shared WebSocket carrying the workspace doc + every open
 * note doc via Hocuspocus multiplexing. Also the single source of the
 * user-facing sync status (the trust chip).
 */
import * as Y from "yjs";
import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { create } from "zustand";
import { SYNC_PATH, parseDocName } from "@hydra-notes/shared";
import { fetchSyncToken, SyncTokenError, clearSyncTokens } from "./auth";

export type SyncStatus = "local" | "connecting" | "synced" | "syncing" | "offline";

interface SyncStateStore {
  status: SyncStatus;
  /** docs with local changes not yet acknowledged by the server */
  dirtyDocs: number;
  set: (patch: Partial<Pick<SyncStateStore, "status" | "dirtyDocs">>) => void;
}

export const useSyncState = create<SyncStateStore>((set) => ({
  status: "local",
  dirtyDocs: 0,
  set: (patch) => set(patch),
}));

const CURSOR_COLORS = [
  ["#1db386", "#0e5943"],
  ["#4a9eda", "#1e4a6b"],
  ["#c678dd", "#5c2f6b"],
  ["#e5a03c", "#6b4a15"],
  ["#e06c75", "#6b2f34"],
] as const;

/** Stable per-browser identity for presence cursors. */
function deviceIdentity(): { name: string; color: string; colorLight: string } {
  let colorIdx = Number(localStorage.getItem("hn.deviceColor"));
  if (!Number.isInteger(colorIdx) || colorIdx < 0 || colorIdx >= CURSOR_COLORS.length) {
    colorIdx = Math.floor(Math.random() * CURSOR_COLORS.length);
    localStorage.setItem("hn.deviceColor", String(colorIdx));
  }
  const ua = navigator.userAgent;
  const browser = /firefox/i.test(ua)
    ? "Firefox"
    : /edg\//i.test(ua)
      ? "Edge"
      : /chrome|chromium/i.test(ua)
        ? "Chrome"
        : /safari/i.test(ua)
          ? "Safari"
          : "Browser";
  const os = /windows/i.test(ua)
    ? "Windows"
    : /mac/i.test(ua)
      ? "macOS"
      : /android/i.test(ua)
        ? "Android"
        : /iphone|ipad/i.test(ua)
          ? "iOS"
          : /linux/i.test(ua)
            ? "Linux"
            : "";
  const [color, colorLight] = CURSOR_COLORS[colorIdx]!;
  return { name: os ? `${browser} on ${os}` : browser, color, colorLight };
}

/**
 * Identity for SHARED docs: collaborators need to know who a cursor belongs
 * to, so the label is the person, not the device, and the color is stable
 * per user (same color on every device, and for everyone looking at them).
 */
export function userIdentity(userId: string, displayName: string): { name: string; color: string; colorLight: string } {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  const [color, colorLight] = CURSOR_COLORS[h % CURSOR_COLORS.length]!;
  return { name: displayName, color, colorLight };
}

export interface AttachOpts {
  /** Space this doc belongs to; the token is minted for it. Absent = personal. */
  spaceId?: string;
  /** Presence identity; defaults to the per-device one used for personal docs. */
  identity?: { name: string; color: string; colorLight: string };
  /** Fired when the sync server or token mint refuses this doc. */
  onAuthFailed?: (err: SyncTokenError) => void;
}

export interface Attachment {
  release: () => void;
  /** Resolves on the first server sync. Never resolves while offline. */
  whenSynced: Promise<void>;
}

export class SyncManager {
  private socket: HocuspocusProviderWebsocket;
  private providers = new Map<string, HocuspocusProvider>();
  private attachments = new Map<string, Attachment>();
  private dirty = new Set<string>();
  private destroyed = false;

  constructor() {
    const wsUrl = `${location.origin.replace(/^http/, "ws")}${SYNC_PATH}`;
    this.socket = new HocuspocusProviderWebsocket({
      url: wsUrl,
      delay: 1000,
      factor: 1.6,
      maxDelay: 20_000,
    });
    this.socket.on("status", ({ status }: { status: string }) => {
      if (this.destroyed) return;
      if (status === "connected") this.recomputeStatus();
      else useSyncState.getState().set({ status: navigator.onLine ? "connecting" : "offline" });
    });
    window.addEventListener("online", this.onNetChange);
    window.addEventListener("offline", this.onNetChange);
    useSyncState.getState().set({ status: "connecting", dirtyDocs: 0 });
  }

  private onNetChange = () => {
    if (this.destroyed) return;
    if (!navigator.onLine) {
      useSyncState.getState().set({ status: "offline" });
    } else {
      useSyncState.getState().set({ status: "connecting" });
      this.socket.connect();
    }
  };

  attach(docName: string, doc: Y.Doc, opts?: AttachOpts): Attachment {
    const existing = this.attachments.get(docName);
    if (existing) return existing;

    let resolveSynced: () => void = () => {};
    const whenSynced = new Promise<void>((r) => (resolveSynced = r));

    const provider = new HocuspocusProvider({
      websocketProvider: this.socket,
      name: docName,
      document: doc,
      token: async () => {
        try {
          return await fetchSyncToken(opts?.spaceId);
        } catch (err) {
          if (err instanceof SyncTokenError && err.code !== "network") opts?.onAuthFailed?.(err);
          throw err;
        }
      },
      onSynced: () => {
        this.dirty.delete(docName);
        this.recomputeStatus();
        resolveSynced();
      },
      onAuthenticationFailed: () => {
        // stale cached token (revoked mid-TTL, kicked socket): drop it so the
        // next attempt mints fresh, and surface the failure
        clearSyncTokens(opts?.spaceId);
        opts?.onAuthFailed?.(new SyncTokenError("sync server refused this doc", "forbidden", opts?.spaceId));
      },
    });
    // presence: your other devices' cursors, or named members in shared docs
    provider.setAwarenessField("user", opts?.identity ?? deviceIdentity());
    // track unacknowledged local edits for the "syncing / saved" chip
    const onUpdate = (_u: Uint8Array, _o: unknown, _d: Y.Doc, tr: Y.Transaction) => {
      if (!tr.local) return;
      this.dirty.add(docName);
      this.recomputeStatus();
      queueMicrotask(() => this.watchAck(docName, provider));
    };
    doc.on("update", onUpdate);
    provider.attach();
    this.providers.set(docName, provider);
    const attachment: Attachment = {
      whenSynced,
      release: () => {
        doc.off("update", onUpdate);
        this.attachments.delete(docName);
        this.detach(docName);
      },
    };
    this.attachments.set(docName, attachment);
    return attachment;
  }

  private ackTimers = new Map<string, ReturnType<typeof setInterval>>();
  private watchAck(docName: string, provider: HocuspocusProvider) {
    if (this.ackTimers.has(docName)) return;
    const timer = setInterval(() => {
      const p = provider as unknown as { unsyncedChanges?: number };
      const pending = typeof p.unsyncedChanges === "number" ? p.unsyncedChanges : 0;
      if (pending === 0 && this.socket.status === "connected") {
        this.dirty.delete(docName);
        clearInterval(timer);
        this.ackTimers.delete(docName);
        this.recomputeStatus();
      }
    }, 400);
    this.ackTimers.set(docName, timer);
  }

  /** Awareness instance for a doc (remote cursors), if it's attached. */
  getAwareness(docName: string): unknown {
    return this.providers.get(docName)?.awareness ?? null;
  }

  /** True while any of a space's docs still has unacknowledged local edits. */
  hasPendingFor(spaceId: string): boolean {
    if (this.socket.status !== "connected") return true;
    for (const [name, p] of this.providers) {
      if (parseDocName(name)?.spaceId !== spaceId) continue;
      const pending = (p as unknown as { unsyncedChanges?: number }).unsyncedChanges ?? 0;
      if (pending > 0) return true;
    }
    return false;
  }

  private detach(docName: string) {
    const p = this.providers.get(docName);
    if (p) {
      p.destroy();
      this.providers.delete(docName);
    }
    this.dirty.delete(docName);
    this.recomputeStatus();
  }

  private recomputeStatus() {
    if (this.destroyed) return;
    const st = useSyncState.getState();
    if (!navigator.onLine || this.socket.status !== "connected") {
      st.set({ status: navigator.onLine ? "connecting" : "offline", dirtyDocs: this.dirty.size });
      return;
    }
    st.set({ status: this.dirty.size > 0 ? "syncing" : "synced", dirtyDocs: this.dirty.size });
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener("online", this.onNetChange);
    window.removeEventListener("offline", this.onNetChange);
    for (const t of this.ackTimers.values()) clearInterval(t);
    this.ackTimers.clear();
    for (const p of this.providers.values()) p.destroy();
    this.providers.clear();
    this.attachments.clear();
    this.socket.destroy();
    useSyncState.getState().set({ status: "local", dirtyDocs: 0 });
  }
}
